// Schema-driven inputs for each contract type. The frontend renders
// these fields dynamically. On submit they are sent as a `details` dict
// to the backend, which formats them into the LLM prompt.
//
// Field types: "text", "textarea", "date", "number", "select"
//
// Convention: when a contract has identifiable parties (Party A / B,
// Buyer / Seller, etc.), each party MUST also expose a Civil ID field
// (Kuwaiti civil ID = 12 digits) right next to the name. The field key
// follows the pattern "<role>_civil_id".

const civil = (key, en, ar) => ({
  key,
  type: "text",
  en,
  ar,
  inputMode: "numeric",
  pattern: "[0-9]{12}",
  maxLength: 12,
  en_ph: "12-digit civil ID",
  ar_ph: "الرقم المدني (12 رقم)",
});

export const CONTRACT_SCHEMAS = {
  nda: [
    { key: "disclosing_party", type: "text", en: "Disclosing Party", ar: "الطرف المُفصِح", required: true },
    civil("disclosing_party_civil_id", "Disclosing Party Civil ID", "الرقم المدني للطرف المُفصِح"),
    { key: "receiving_party", type: "text", en: "Receiving Party", ar: "الطرف المتلقّي", required: true },
    civil("receiving_party_civil_id", "Receiving Party Civil ID", "الرقم المدني للطرف المتلقّي"),
    { key: "purpose", type: "textarea", en: "Purpose / Subject", ar: "الغرض / الموضوع", required: true,
      en_ph: "e.g. discussions about a potential business relationship",
      ar_ph: "مثال: مناقشة علاقة تجارية محتملة" },
    { key: "term_years", type: "number", en: "Term (years)", ar: "المدة (بالسنوات)" },
  ],

  employment: [
    { key: "employer", type: "text", en: "Employer (Company)", ar: "صاحب العمل (الشركة)", required: true },
    civil("employer_civil_id", "Employer Civil ID / Commercial No.", "الرقم المدني / السجل التجاري لصاحب العمل"),
    { key: "employee", type: "text", en: "Employee Name", ar: "اسم الموظف", required: true },
    civil("employee_civil_id", "Employee Civil ID", "الرقم المدني للموظف"),
    { key: "position", type: "text", en: "Position / Job Title", ar: "المسمى الوظيفي", required: true },
    { key: "salary", type: "text", en: "Salary (amount & currency)", ar: "الراتب (المبلغ والعملة)",
      en_ph: "e.g. 1,500 KWD per month", ar_ph: "مثال: 1,500 د.ك شهريًا" },
    { key: "start_date", type: "date", en: "Start Date", ar: "تاريخ المباشرة" },
  ],

  service: [
    { key: "service_provider", type: "text", en: "Service Provider", ar: "مقدّم الخدمة", required: true },
    civil("service_provider_civil_id", "Provider Civil ID / Commercial No.", "الرقم المدني / السجل التجاري لمقدّم الخدمة"),
    { key: "client", type: "text", en: "Client", ar: "العميل", required: true },
    civil("client_civil_id", "Client Civil ID", "الرقم المدني للعميل"),
    { key: "services", type: "textarea", en: "Description of Services", ar: "وصف الخدمات", required: true },
    { key: "fee", type: "text", en: "Fee / Price", ar: "الأتعاب / السعر",
      en_ph: "e.g. 5,000 KWD total", ar_ph: "مثال: 5,000 د.ك إجمالاً" },
    { key: "term", type: "text", en: "Term / End Date", ar: "المدة / تاريخ الانتهاء" },
    { key: "warranty_period", type: "text", en: "Warranty Period", ar: "مدة الضمان",
      en_ph: "e.g. 90 days from delivery", ar_ph: "مثال: 90 يوماً من تاريخ التسليم" },
  ],

  consultancy: [
    { key: "consultant", type: "text", en: "Consultant", ar: "المستشار", required: true },
    civil("consultant_civil_id", "Consultant Civil ID", "الرقم المدني للمستشار"),
    { key: "client", type: "text", en: "Client", ar: "العميل", required: true },
    civil("client_civil_id", "Client Civil ID", "الرقم المدني للعميل"),
    { key: "scope", type: "textarea", en: "Scope of Consultancy", ar: "نطاق الاستشارة", required: true },
    { key: "fee", type: "text", en: "Fee", ar: "الأتعاب" },
    { key: "term", type: "text", en: "Term", ar: "المدة" },
    { key: "warranty_period", type: "text", en: "Warranty Period", ar: "مدة الضمان",
      en_ph: "e.g. 30 days after report delivery", ar_ph: "مثال: 30 يوماً بعد تسليم التقرير" },
  ],

  lease: [
    { key: "landlord", type: "text", en: "Landlord (Lessor)", ar: "المؤجر", required: true },
    civil("landlord_civil_id", "Landlord Civil ID", "الرقم المدني للمؤجر"),
    { key: "tenant", type: "text", en: "Tenant (Lessee)", ar: "المستأجر", required: true },
    civil("tenant_civil_id", "Tenant Civil ID", "الرقم المدني للمستأجر"),
    { key: "property_address", type: "textarea", en: "Property Address & Description", ar: "عنوان ووصف العقار", required: true },
    { key: "rent_amount", type: "text", en: "Rent Amount", ar: "قيمة الإيجار",
      en_ph: "e.g. 600 KWD per month", ar_ph: "مثال: 600 د.ك شهريًا" },
    { key: "term", type: "text", en: "Lease Term", ar: "مدة الإيجار",
      en_ph: "e.g. 1 year, renewable", ar_ph: "مثال: سنة قابلة للتجديد" },
  ],

  // Residential lease — Kuwaiti رقم سكني
  lease_residential: [
    { key: "landlord", type: "text", en: "Landlord", ar: "المؤجر", required: true },
    civil("landlord_civil_id", "Landlord Civil ID", "الرقم المدني للمؤجر"),
    { key: "tenant", type: "text", en: "Tenant", ar: "المستأجر", required: true },
    civil("tenant_civil_id", "Tenant Civil ID", "الرقم المدني للمستأجر"),
    { key: "property_address", type: "textarea", en: "Property Address (Area / Block / Street / House)", ar: "عنوان العقار (المنطقة / القطعة / الشارع / المنزل)", required: true },
    { key: "rent_amount", type: "text", en: "Monthly Rent (KWD)", ar: "الإيجار الشهري (د.ك)", required: true },
    { key: "term", type: "text", en: "Lease Term", ar: "مدة الإيجار", en_ph: "e.g. 1 year, renewable", ar_ph: "مثال: سنة قابلة للتجديد" },
    { key: "security_deposit", type: "text", en: "Security Deposit", ar: "مبلغ التأمين" },
  ],

  // Commercial lease — تأجير محل تجاري
  lease_commercial: [
    { key: "landlord", type: "text", en: "Landlord", ar: "المؤجر", required: true },
    civil("landlord_civil_id", "Landlord Civil ID / Commercial No.", "الرقم المدني / السجل التجاري للمؤجر"),
    { key: "tenant", type: "text", en: "Tenant (Business)", ar: "المستأجر (المنشأة)", required: true },
    civil("tenant_civil_id", "Tenant Commercial No.", "السجل التجاري للمستأجر"),
    { key: "property_address", type: "textarea", en: "Premises Address & Area (m²)", ar: "عنوان المحل والمساحة (م²)", required: true },
    { key: "permitted_use", type: "text", en: "Permitted Use / Activity", ar: "النشاط المسموح به", required: true },
    { key: "rent_amount", type: "text", en: "Annual Rent (KWD)", ar: "الإيجار السنوي (د.ك)", required: true },
    { key: "term", type: "text", en: "Lease Term", ar: "مدة الإيجار" },
  ],

  sales: [
    { key: "seller", type: "text", en: "Seller", ar: "البائع", required: true },
    civil("seller_civil_id", "Seller Civil ID", "الرقم المدني للبائع"),
    { key: "buyer", type: "text", en: "Buyer", ar: "المشتري", required: true },
    civil("buyer_civil_id", "Buyer Civil ID", "الرقم المدني للمشتري"),
    { key: "goods", type: "textarea", en: "Goods / Asset Description", ar: "وصف البضائع / الأصول", required: true },
    { key: "price", type: "text", en: "Price", ar: "السعر", required: true },
    { key: "delivery_date", type: "date", en: "Delivery Date", ar: "تاريخ التسليم" },
    { key: "warranty_period", type: "text", en: "Warranty Period", ar: "مدة الضمان",
      en_ph: "e.g. 12 months from delivery", ar_ph: "مثال: 12 شهراً من تاريخ التسليم" },
  ],

  // Vehicle sale — بيع سيارة (مع رقم اللوحة و رقم الهيكل)
  sale_vehicle: [
    { key: "seller", type: "text", en: "Seller", ar: "البائع", required: true },
    civil("seller_civil_id", "Seller Civil ID", "الرقم المدني للبائع"),
    { key: "buyer", type: "text", en: "Buyer", ar: "المشتري", required: true },
    civil("buyer_civil_id", "Buyer Civil ID", "الرقم المدني للمشتري"),
    { key: "vehicle_make_model", type: "text", en: "Make / Model / Year", ar: "الماركة / الموديل / سنة الصنع", required: true,
      en_ph: "e.g. Toyota Camry 2022", ar_ph: "مثال: تويوتا كامري 2022" },
    { key: "plate_number", type: "text", en: "Plate Number", ar: "رقم اللوحة", required: true },
    { key: "chassis_number", type: "text", en: "Chassis (VIN) Number", ar: "رقم الهيكل (VIN)", required: true },
    { key: "price", type: "text", en: "Price", ar: "السعر", required: true },
    { key: "warranty_period", type: "text", en: "Warranty Period", ar: "مدة الضمان",
      en_ph: "e.g. as-is, or 3 months mechanical", ar_ph: "مثال: كما هي، أو 3 أشهر ميكانيكي" },
  ],

  // Construction / contracting — مقاولة بناء
  construction: [
    { key: "owner", type: "text", en: "Owner", ar: "المالك", required: true },
    civil("owner_civil_id", "Owner Civil ID", "الرقم المدني للمالك"),
    { key: "contractor", type: "text", en: "Contractor", ar: "المقاول", required: true },
    civil("contractor_civil_id", "Contractor Commercial No.", "السجل التجاري للمقاول"),
    { key: "project_address", type: "textarea", en: "Project Address & Description", ar: "موقع المشروع ووصفه", required: true },
    { key: "scope_of_work", type: "textarea", en: "Scope of Work", ar: "نطاق الأعمال", required: true },
    { key: "total_value", type: "text", en: "Total Contract Value", ar: "القيمة الإجمالية", required: true },
    { key: "completion_period", type: "text", en: "Completion Period", ar: "مدة التنفيذ" },
    { key: "warranty_period", type: "text", en: "Warranty Period", ar: "مدة الضمان",
      en_ph: "e.g. 12 months from handover", ar_ph: "مثال: 12 شهراً من تاريخ التسليم" },
  ],

  // Loan agreement — عقد قرض
  loan: [
    { key: "lender", type: "text", en: "Lender", ar: "المُقرِض", required: true },
    civil("lender_civil_id", "Lender Civil ID", "الرقم المدني للمُقرِض"),
    { key: "borrower", type: "text", en: "Borrower", ar: "المُقترِض", required: true },
    civil("borrower_civil_id", "Borrower Civil ID", "الرقم المدني للمُقترِض"),
    { key: "principal", type: "text", en: "Principal Amount (KWD)", ar: "أصل المبلغ (د.ك)", required: true },
    { key: "repayment_terms", type: "textarea", en: "Repayment Terms", ar: "شروط السداد", required: true,
      en_ph: "e.g. 12 monthly installments of 1,000 KWD each",
      ar_ph: "مثال: 12 قسطًا شهريًا قيمة كل منها 1,000 د.ك" },
    { key: "due_date", type: "date", en: "Final Due Date", ar: "تاريخ السداد النهائي" },
  ],

  partnership: [
    { key: "partner_a", type: "text", en: "Partner A", ar: "الشريك الأول", required: true },
    civil("partner_a_civil_id", "Partner A Civil ID", "الرقم المدني للشريك الأول"),
    { key: "partner_b", type: "text", en: "Partner B", ar: "الشريك الثاني", required: true },
    civil("partner_b_civil_id", "Partner B Civil ID", "الرقم المدني للشريك الثاني"),
    { key: "partnership_name", type: "text", en: "Partnership / Business Name", ar: "اسم الشراكة / النشاط" },
    { key: "purpose", type: "textarea", en: "Purpose & Business Activity", ar: "الغرض والنشاط التجاري", required: true },
    { key: "capital", type: "textarea", en: "Capital Contributions", ar: "المساهمات في رأس المال",
      en_ph: "e.g. Partner A: 60% / 30,000 KWD; Partner B: 40% / 20,000 KWD",
      ar_ph: "مثال: الشريك الأول 60% / 30,000 د.ك؛ الشريك الثاني 40% / 20,000 د.ك" },
    { key: "profit_sharing", type: "text", en: "Profit & Loss Sharing", ar: "توزيع الأرباح والخسائر" },
  ],

  mou: [
    { key: "party_a", type: "text", en: "Party A", ar: "الطرف الأول", required: true },
    civil("party_a_civil_id", "Party A Civil ID", "الرقم المدني للطرف الأول"),
    { key: "party_b", type: "text", en: "Party B", ar: "الطرف الثاني", required: true },
    civil("party_b_civil_id", "Party B Civil ID", "الرقم المدني للطرف الثاني"),
    { key: "subject", type: "textarea", en: "Subject of Cooperation", ar: "موضوع التعاون", required: true },
    { key: "duration", type: "text", en: "Duration", ar: "المدة" },
  ],

  power_of_attorney: [
    { key: "principal", type: "text", en: "Principal (Grantor)", ar: "الموكِّل", required: true },
    civil("principal_civil_id", "Principal Civil ID", "الرقم المدني للموكِّل"),
    { key: "attorney", type: "text", en: "Attorney-in-Fact", ar: "الوكيل", required: true },
    civil("attorney_civil_id", "Attorney Civil ID", "الرقم المدني للوكيل"),
    { key: "powers", type: "textarea", en: "Powers Granted", ar: "الصلاحيات الممنوحة", required: true },
    { key: "duration", type: "text", en: "Duration", ar: "المدة" },
  ],

  // Special / limited power of attorney — توكيل خاص
  power_of_attorney_special: [
    { key: "principal", type: "text", en: "Principal", ar: "الموكِّل", required: true },
    civil("principal_civil_id", "Principal Civil ID", "الرقم المدني للموكِّل"),
    { key: "attorney", type: "text", en: "Attorney-in-Fact", ar: "الوكيل", required: true },
    civil("attorney_civil_id", "Attorney Civil ID", "الرقم المدني للوكيل"),
    { key: "specific_matter", type: "textarea", en: "Specific Matter / Single Action Authorised", ar: "المسألة المحددة / التصرف المُفوَّض", required: true,
      en_ph: "e.g. sell vehicle plate no. 12345, follow case no. 678/2026 in Court of First Instance",
      ar_ph: "مثال: بيع سيارة لوحة 12345، أو متابعة القضية 678/2026 أمام محكمة أول درجة" },
    { key: "duration", type: "text", en: "Duration / Expiry", ar: "المدة / تاريخ الانتهاء" },
  ],

  shareholders: [
    { key: "company", type: "text", en: "Company Name", ar: "اسم الشركة", required: true },
    { key: "shareholders", type: "textarea", en: "Shareholders & Shareholdings", ar: "المساهمون والحصص", required: true,
      en_ph: "e.g. Mr. X (Civil ID 123…): 60%; Ms. Y (Civil ID 456…): 40%",
      ar_ph: "مثال: السيد س (الرقم المدني 123…) 60%؛ السيدة ص (الرقم المدني 456…) 40%" },
    { key: "voting", type: "textarea", en: "Voting Rights & Reserved Matters", ar: "حقوق التصويت والمسائل المحفوظة" },
    { key: "transfer", type: "textarea", en: "Share Transfer Restrictions", ar: "قيود نقل الحصص" },
  ],

  settlement: [
    { key: "party_a", type: "text", en: "Party A (Claimant)", ar: "الطرف الأول (المدّعي)", required: true },
    civil("party_a_civil_id", "Party A Civil ID", "الرقم المدني للطرف الأول"),
    { key: "party_b", type: "text", en: "Party B (Respondent)", ar: "الطرف الثاني (المدّعى عليه)", required: true,
    },
    civil("party_b_civil_id", "Party B Civil ID", "الرقم المدني للطرف الثاني"),
    { key: "dispute", type: "textarea", en: "Dispute Description", ar: "وصف النزاع", required: true },
    { key: "settlement_amount", type: "text", en: "Settlement Amount", ar: "قيمة التسوية" },
    { key: "release_scope", type: "textarea", en: "Release of Claims (scope)", ar: "نطاق التنازل عن المطالبات" },
  ],

  // Quittance / discharge — إقرار وتنازل
  quittance: [
    { key: "discharger", type: "text", en: "Person Granting Discharge", ar: "المُقِر بالاستلام", required: true },
    civil("discharger_civil_id", "Discharger Civil ID", "الرقم المدني للمُقِر"),
    { key: "released_party", type: "text", en: "Party Being Discharged", ar: "الطرف الذي يتم إبراؤه", required: true },
    civil("released_party_civil_id", "Discharged Party Civil ID", "الرقم المدني للطرف المُبرأ"),
    { key: "subject_matter", type: "textarea", en: "Subject Matter / Amount Received", ar: "موضوع الإقرار / المبلغ المُستلَم", required: true,
      en_ph: "e.g. received 5,000 KWD as full and final settlement of …",
      ar_ph: "مثال: استلام مبلغ 5,000 د.ك كتسوية نهائية كاملة عن …" },
  ],

  // Discharge / clearance certificate — ورقة براءة ذمة
  discharge: [
    { key: "issuer", type: "text", en: "Issuer (Creditor)", ar: "مُصدِر البراءة (الدائن)", required: true,
      en_ph: "Party confirming the debt is cleared",
      ar_ph: "الطرف الذي يؤكد سداد الذمة" },
    civil("issuer_civil_id", "Issuer Civil ID", "الرقم المدني للمُصدِر"),
    { key: "discharged_party", type: "text", en: "Discharged Party (Debtor)", ar: "الطرف المُبرأ (المدين)", required: true },
    civil("discharged_civil_id", "Discharged Party Civil ID", "الرقم المدني للطرف المُبرأ"),
    { key: "debt_subject", type: "textarea", en: "Subject of the Debt", ar: "موضوع الدَّين / الالتزام", required: true,
      en_ph: "e.g. car-loan principal of 3,500 KWD, service-fee arrears, lease balance…",
      ar_ph: "مثال: قيمة قرض سيارة 3,500 د.ك، متأخر أتعاب خدمات، رصيد إيجار…" },
    { key: "amount", type: "text", en: "Total Amount Settled", ar: "إجمالي المبلغ المُسدَّد", required: true,
      en_ph: "e.g. 3,500 KWD", ar_ph: "مثال: 3,500 د.ك" },
    { key: "payment_method", type: "text", en: "Payment Method & Reference", ar: "طريقة السداد والمرجع",
      en_ph: "e.g. K-Net transfer ref #9912, cheque #4421",
      ar_ph: "مثال: تحويل كي-نت رقم 9912، شيك رقم 4421" },
    { key: "settlement_date", type: "date", en: "Settlement Date", ar: "تاريخ السداد" },
    { key: "scope_note", type: "textarea", en: "Scope of Release (optional)", ar: "نطاق البراءة (اختياري)",
      en_ph: "e.g. release covers ALL prior dealings up to this date, or only the above debt",
      ar_ph: "مثال: البراءة تشمل كل المعاملات السابقة حتى تاريخه، أو الدَّين المذكور فقط" },
  ],

  // Marriage contract — عقد زواج (per Kuwait Personal Status Law)
  marriage: [
    { key: "husband", type: "text", en: "Husband (Full Name)", ar: "الزوج (الاسم الكامل)", required: true },
    civil("husband_civil_id", "Husband Civil ID", "الرقم المدني للزوج"),
    { key: "wife", type: "text", en: "Wife (Full Name)", ar: "الزوجة (الاسم الكامل)", required: true },
    civil("wife_civil_id", "Wife Civil ID", "الرقم المدني للزوجة"),
    { key: "wali", type: "text", en: "Wife's Guardian (Wali)", ar: "ولي الزوجة" },
    { key: "dowry_advance", type: "text", en: "Advance Dowry (Mahr Muqaddam)", ar: "المهر المعجَّل" },
    { key: "dowry_deferred", type: "text", en: "Deferred Dowry (Mahr Mu'akhar)", ar: "المهر المؤجَّل" },
    { key: "witnesses", type: "textarea", en: "Witnesses (names & civil IDs)", ar: "الشهود (الأسماء والأرقام المدنية)" },
  ],

  custom: [
    { key: "parties", type: "textarea", en: "Parties (names & civil IDs)", ar: "الأطراف (الأسماء والأرقام المدنية)", required: true,
      en_ph: "e.g. ____ Holding Co. (CR 12345) and Mr./Mrs. ____ (Civil ID ____)",
      ar_ph: "مثال: شركة ____ القابضة (سجل تجاري 12345) والسيد/ة ____ (الرقم المدني ____)" },
    { key: "subject", type: "textarea", en: "Subject / Purpose", ar: "الموضوع / الغرض", required: true },
    { key: "key_terms", type: "textarea", en: "Key Terms", ar: "البنود الجوهرية", required: true,
      en_ph: "Subject matter, obligations, consideration, term, confidentiality, special clauses…",
      ar_ph: "الموضوع، الالتزامات، المقابل، المدة، السرية، البنود الخاصة…" },
  ],
};
